import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { FileText, CheckCircle, ChefHat, CreditCard, AlertTriangle, Scale, Mail, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.6, ease: "easeOut" }
  }
};

// Animated Section Component
const AnimatedSection = ({ children, className = "" }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  
  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={fadeInUp}
      className={className}
    >
      {children}
    </motion.div>
  );
};

// Terms Section Component
const TermsSection = ({ icon: Icon, title, children, testId }) => {
  return (
    <AnimatedSection className="mb-12">
      <div className="flex items-start gap-4" data-testid={testId}>
        <div className="flex-shrink-0 w-12 h-12 bg-[#F3F4F6] flex items-center justify-center">
          <Icon className="w-6 h-6 text-[#0055FF]" />
        </div>
        <div>
          <h2 className="font-heading text-xl sm:text-2xl font-bold tracking-tight text-[#121212] mb-4">
            {title}
          </h2>
          <div className="text-gray-600 leading-relaxed font-light space-y-3">
            {children}
          </div>
        </div>
      </div>
    </AnimatedSection>
  );
};

// Header Component
const Header = () => {
  return (
    <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-white/80 border-b border-gray-100">
      <div className="max-w-7xl mx-auto px-6 sm:px-12">
        <div className="flex items-center justify-between h-16 sm:h-20">
          <a href="/" className="flex items-center gap-2" data-testid="header-logo">
            <span className="font-heading text-xl sm:text-2xl font-bold tracking-tight text-[#121212]">
              LabrHood<span className="text-[#0055FF]">Labs</span>
            </span>
          </a>
          <Button 
            asChild
            variant="ghost"
            className="text-sm font-medium"
            data-testid="back-home-btn"
          >
            <a href="/">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </a>
          </Button>
        </div>
      </div>
    </header>
  );
};

// Footer Component
const Footer = () => {
  return (
    <footer className="py-12 bg-white border-t border-gray-100" data-testid="footer-section">
      <div className="max-w-7xl mx-auto px-6 sm:px-12">
        <div className="flex flex-col items-center justify-center text-center">
          <span className="font-heading text-lg font-bold tracking-tight text-[#121212] mb-4">
            LabrHood<span className="text-[#0055FF]">Labs</span>
          </span>
          <p className="text-sm text-gray-500">
            &copy; 2026 LabrHood Labs
          </p>
        </div>
      </div>
    </footer>
  );
};

export default function YesChefTerms() {
  const lastUpdated = "June 7, 2026";

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <main className="pt-32 pb-24">
        <div className="max-w-4xl mx-auto px-6 sm:px-12">
          {/* Hero */}
          <AnimatedSection className="mb-16">
            <span className="text-xs tracking-[0.2em] uppercase font-bold text-gray-500 mb-4 block">
              Legal
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-[#121212] mb-6" data-testid="terms-headline">
              Terms of Service — Yes Chef
            </h1>
            <p className="text-sm text-gray-500">
              Last Updated: {lastUpdated}
            </p>
          </AnimatedSection>

          {/* Introduction */}
          <AnimatedSection className="mb-12">
            <p className="text-gray-600 leading-relaxed font-light">
              Please read these Terms of Service ("Terms") carefully before using Yes Chef ("the App"), a product of LabrHood Labs. By downloading, installing, or using the App, you agree to be bound by these Terms.
            </p>
          </AnimatedSection>

          <div className="thin-divider mb-12" />

          {/* Acceptance of Terms */}
          <TermsSection 
            icon={CheckCircle} 
            title="Acceptance of Terms" 
            testId="terms-acceptance"
          >
            <p>
              By accessing or using Yes Chef, you confirm that you have read, understood, and agree to be bound by these Terms and our Privacy Policy. If you do not agree to these Terms, you may not use the App.
            </p>
            <p>
              We reserve the right to modify these Terms at any time. Continued use of the App after changes constitutes acceptance of the modified Terms.
            </p>
          </TermsSection>

          {/* AI-Generated Culinary Suggestions */}
          <TermsSection 
            icon={ChefHat} 
            title="AI-Generated Culinary Suggestions" 
            testId="terms-ai-suggestions"
          >
            <p>
              Yes Chef provides AI-generated recipes, cooking suggestions, and culinary guidance <strong>for convenience and informational purposes only</strong>. While we strive to provide helpful and accurate information, you acknowledge and agree that:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li><strong>Allergies and Dietary Restrictions:</strong> AI-generated content may not account for all food allergies, intolerances, or dietary restrictions. You are solely responsible for reviewing ingredients and ensuring recipes are safe for you and anyone you cook for.</li>
              <li><strong>Food Safety:</strong> You should use your own judgment regarding food handling, cooking temperatures, storage, and expiration. The App does not replace proper food safety practices.</li>
              <li><strong>Nutritional Information:</strong> Any nutritional estimates provided are approximate and should not be relied upon for medical or dietary planning.</li>
              <li><strong>Not Professional Advice:</strong> Yes Chef does not provide medical, nutritional, or professional culinary advice. Consult appropriate professionals for specific dietary or health needs.</li>
            </ul>
          </TermsSection>

          {/* Subscription and Billing */}
          <TermsSection 
            icon={CreditCard} 
            title="Subscription and Billing" 
            testId="terms-subscription"
          >
            <p>
              Yes Chef offers subscription memberships that provide access to premium features.
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li><strong>Free Trial:</strong> New subscribers may be eligible for a 3-day free trial. If you do not cancel before the trial ends, your subscription will automatically convert to a paid subscription.</li>
              <li><strong>Auto-Renewal:</strong> Subscriptions automatically renew at the end of each billing period (monthly or annually) unless you cancel at least 24 hours before the renewal date.</li>
              <li><strong>Payment Processing:</strong> All payments are processed through the Apple App Store or Google Play Store. We do not have access to your payment card details.</li>
              <li><strong>Cancellation:</strong> You may cancel your subscription at any time through your device's subscription settings. Cancellation takes effect at the end of the current billing period.</li>
              <li><strong>Refunds:</strong> Refund requests are handled by Apple or Google according to their respective policies.</li>
            </ul>
          </TermsSection>

          {/* Disclaimer of Warranties */}
          <TermsSection 
            icon={AlertTriangle} 
            title="Disclaimer of Warranties" 
            testId="terms-disclaimer"
          >
            <p>
              THE APP IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
            </p>
            <p>
              We do not warrant that the App will be uninterrupted, error-free, or free of harmful components. We make no guarantees regarding the accuracy, completeness, or reliability of any content, including AI-generated recipes and suggestions.
            </p>
          </TermsSection>

          {/* Limitation of Liability */}
          <TermsSection 
            icon={Scale} 
            title="Limitation of Liability" 
            testId="terms-liability"
          >
            <p>
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, LABRHOOD LABS AND ITS AFFILIATES, OFFICERS, EMPLOYEES, AND AGENTS SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Personal injury or illness resulting from recipes or cooking suggestions</li>
              <li>Allergic reactions or adverse health effects</li>
              <li>Loss of data or service interruptions</li>
              <li>Any damages arising from your use of or inability to use the App</li>
            </ul>
            <p className="mt-4">
              Our total liability for any claims arising from your use of the App shall not exceed the amount you paid for the App in the twelve (12) months preceding the claim.
            </p>
          </TermsSection>

          {/* Governing Law */}
          <AnimatedSection className="mb-12">
            <h3 className="font-heading text-xl font-bold tracking-tight text-[#121212] mb-4">
              Governing Law
            </h3>
            <p className="text-gray-600 leading-relaxed font-light">
              These Terms shall be governed by and construed in accordance with the laws of the United States, without regard to conflict of law principles.
            </p>
          </AnimatedSection>

          <div className="thin-divider mb-12" />

          {/* Contact Section */}
          <AnimatedSection>
            <div className="bg-[#F9FAFB] p-8 sm:p-12">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 bg-white flex items-center justify-center">
                  <Mail className="w-6 h-6 text-[#0055FF]" />
                </div>
                <div>
                  <h2 className="font-heading text-xl sm:text-2xl font-bold tracking-tight text-[#121212] mb-4">
                    Contact Us
                  </h2>
                  <p className="text-gray-600 leading-relaxed font-light mb-4">
                    For questions about these Terms of Service, contact:
                  </p>
                  <a 
                    href="mailto:sravenscroft80@gmail.com" 
                    className="text-[#0055FF] hover:underline font-medium"
                    data-testid="contact-email"
                  >
                    sravenscroft80@gmail.com
                  </a>
                </div>
              </div>
            </div>
          </AnimatedSection>

          {/* Links */}
          <AnimatedSection className="mt-12">
            <div className="flex flex-wrap gap-4">
              <Button 
                asChild
                variant="outline"
                className="border-gray-300 text-gray-700 hover:bg-gray-50"
                data-testid="privacy-link"
              >
                <a href="/yes-chef/privacy">View Privacy Policy</a>
              </Button>
            </div>
          </AnimatedSection>
        </div>
      </main>

      <Footer />
    </div>
  );
}
